import jsPDF from 'jspdf';

export interface MgKennzahlen {
    /** Ziel-Faktor (Soll) */
    multiplikator: number;
    /** Erreichter Faktor (Ist) = Ø Netto-Umsatz ÷ Bruttolohn */
    istMultiplikator: number;
    /** Ist VK-Quote */
    vkQuote: number;
    /** Ziel VK-Quote (z. B. 0.10) */
    vkZiel: number;
    /** Ist Umsatzauslastung */
    umsatzauslastung: number;
    /** Ziel Umsatzauslastung (z. B. 0.75) */
    auslastungZiel: number;
    /** Soll-Stundenumsatz brutto (100 %) */
    basisStdUmsatz: number;
    /** Ist-Stundenumsatz brutto */
    istStdUmsatz: number;
    /** Soll-Stundenumsatz brutto bei 75 % Auslastung */
    stdUmsatz75: number;
    /** Soll Monatsumsatz netto */
    umsatzZielNetto: number;
    /** Ist Monatsumsatz netto */
    umsatzIstNetto: number;
}

export interface GenerateMgFormblattPdfParams {
    employeeName: string;
    inhaberName: string;
    zeitraum: string;
    datum?: string;
    kennzahlen: MgKennzahlen;
    durchschnittsBon: string;
    kundenquote: string;
    massnahme1: string;
    massnahme2: string;
    /** Nächstes Gespräch / Zwischenkontrolle (Anzeige z. B. „15.10.2026“) */
    folgeTermin?: string;
    currencyCode?: string;
}

export interface BuildMgFormblattPdfResult {
    doc: jsPDF;
    blob: Blob;
    filename: string;
}

const fmtNum = (n: number, digits = 2) =>
    Number(n || 0).toLocaleString('de-DE', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    });

const fmtPct = (ratio: number) =>
    `${(Number(ratio || 0) * 100).toLocaleString('de-DE', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
    })} %`;

const fmtEur = (n: number, currency = 'EUR') => `${fmtNum(n, 0)} ${currency}`;

/** Vorzeichen-Zahl, z.B. "+1,50" / "-0,92" */
const fmtSigned = (n: number, digits = 2) =>
    `${n >= 0 ? '+' : ''}${fmtNum(n, digits)}`;

/** Differenz in Prozentpunkten mit Vorzeichen, z.B. "-5,8 Pkt." */
const fmtPkt = (ratio: number) => `${fmtSigned(Number(ratio || 0) * 100, 1)} Pkt.`;

// ==========================================
// ZONEN-BEWERTUNG (DYNAMISIERUNG)
// Identische Schwellen wie getEvaluationLevel im ExecutiveSummaryModal:
// statt binär rot/grün wird die Zielerreichung in 6 Zonen abgestuft.
// ==========================================
export type MgKpiLevelKey = 'exzellent' | 'sehrGut' | 'gut' | 'grenzwertig' | 'kritisch' | 'defizitaer';

export interface MgKpiLevel {
    key: MgKpiLevelKey;
    label: string;
    rgb: [number, number, number];
}

const MG_LEVELS: Record<MgKpiLevelKey, MgKpiLevel> = {
    exzellent: { key: 'exzellent', label: 'Exzellent', rgb: [126, 34, 206] },
    sehrGut: { key: 'sehrGut', label: 'Sehr Gut', rgb: [4, 120, 87] },
    gut: { key: 'gut', label: 'Gut', rgb: [77, 124, 15] },
    grenzwertig: { key: 'grenzwertig', label: 'Grenzwertig', rgb: [180, 83, 9] },
    kritisch: { key: 'kritisch', label: 'Kritisch', rgb: [194, 65, 12] },
    defizitaer: { key: 'defizitaer', label: 'Defizitär', rgb: [185, 28, 28] },
};

/**
 * Zonen-Bewertung einer Kennzahl.
 * 'faktor' = Verhältnis Ist/Ziel (Effizienz-Schwellen),
 * 'vk' & 'auslastung' = absolute Schwellen (wie im Haupt-Modal).
 */
export const getMgKpiLevel = (type: 'faktor' | 'vk' | 'auslastung', ist: number, ziel: number): MgKpiLevel => {
    if (type === 'vk') {
        if (ist >= 0.15) return MG_LEVELS.exzellent;
        if (ist >= 0.10) return MG_LEVELS.sehrGut;
        if (ist >= 0.069) return MG_LEVELS.gut;
        if (ist >= 0.05) return { ...MG_LEVELS.grenzwertig, label: 'Akzeptabel' };
        if (ist >= 0.03) return MG_LEVELS.kritisch;
        return MG_LEVELS.defizitaer;
    }
    if (type === 'auslastung') {
        if (ist >= 0.85) return MG_LEVELS.exzellent;
        if (ist >= 0.75) return MG_LEVELS.sehrGut;
        if (ist >= 0.70) return MG_LEVELS.gut;
        if (ist >= 0.60) return MG_LEVELS.grenzwertig;
        if (ist >= 0.50) return MG_LEVELS.kritisch;
        return MG_LEVELS.defizitaer;
    }
    const ratio = ziel > 0 ? ist / ziel : 0;
    if (ratio >= 1.00) return MG_LEVELS.exzellent;
    if (ratio >= 0.95) return MG_LEVELS.sehrGut;
    if (ratio >= 0.89) return MG_LEVELS.gut;
    if (ratio >= 0.72) return MG_LEVELS.grenzwertig;
    if (ratio >= 0.63) return MG_LEVELS.kritisch;
    return MG_LEVELS.defizitaer;
};

/** Zielerreichung als Quote (0–1+), z. B. 0.74 = 74 % des Ziels erreicht */
export const getMgKpiErreichtQuote = (ist: number, ziel: number): number =>
    ziel > 0 ? ist / ziel : 0;

const todayDe = () => {
    const d = new Date();
    const p = (x: number) => String(x).padStart(2, '0');
    return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}`;
};

// ==========================================
// LEITFADEN-INHALTE — NUR Ansicht im Modal (Gesprächsführer).
// Diese Texte erscheinen bewusst NICHT im Protokoll-PDF,
// damit die unterschriebene Ausdruck-Version keinen Leitfaden/Dummy-Text enthält.
// ==========================================
/** 4 Vorab-Fragen — Selbsteinschätzung für den Mitarbeiter (7 Tage vor dem Gespräch) */
export const MG_VORAB_FRAGEN: Array<{ nr: number; title: string; hint: string }> = [
    {
        nr: 1,
        title: 'Was sind aus deiner Sicht aktuell deine größten Stärken im Salon?',
        hint: 'z. B. Technik, Beratung, Tempo, Team, Kundenbindung …',
    },
    {
        nr: 2,
        title: 'Wo siehst du bei dir aktuell die größten Hürden oder Unsicherheiten?',
        hint: 'z. B. Verkauf, Veredelungen, Zeitdruck, Kasse, Fachwissen …',
    },
    {
        nr: 3,
        title: 'Welches konkrete Monatsziel möchtest du dir für die nächsten 4–6 Wochen setzen?',
        hint: 'Bitte möglichst messbar formulieren (z. B. VK-Quote, Stundenumsatz, Bon …).',
    },
    {
        nr: 4,
        title: 'Wenn du der Unternehmer / die Chefin wärst — was würdest du persönlich in diesem Unternehmen ändern?',
        hint: 'Denk aus der Unternehmerperspektive: Abläufe, Angebot, Team, Qualität, Kundenerlebnis …',
    },
];

export const MG_LEITFADEN = {
    opener: (name: string) =>
        `„Danke, dass du dir die Zeit nimmst, ${name || '[Name]'}. Dieses Gespräch ist für uns beide ein festes Werkzeug zur Standortsicherung. Mein Job als Unternehmer ist es, dir beste Arbeitsbedingungen, moderne Werkzeuge und einen sicheren, fairen Lohn zu garantieren – und deine Aufgabe ist es, deine Leistung am Kunden exzellent abzuliefern.\n\n` +
        `Wir schauen uns heute völlig sachlich die Auswertung aus PEOne an: Wir gleichen deine Selbsteinschätzung mit den realen Systemwerten ab – vom errechneten Multiplikator über die VK-Quote bis zu deinem Stundenumsatz bei 75 % Auslastung. Wir sprechen auch darüber, was du ändern würdest, wenn du hier Unternehmer wärst – weil wir dein Blickwinkel und unternehmerisches Mitdenken ernst nehmen. Wir schauen, wo du stark liegst und wo wir anpacken müssen, damit du deine Vorgaben erreichst. Am Ende vereinbaren wir genau zwei konkrete Maßnahmen für die nächsten drei Monate.“`,
    steps: [
        {
            title: 'Schritt 1: Selbsteinschätzung (ca. 8–10 Min.)',
            body: 'Der Mitarbeiter führt anhand seines Vorbereitungsbogens ein (Stärken, Hürden, Monatsziel). Meist werden Schwachstellen (z. B. Unsicherheit beim Verkauf) bereits von selbst realistisch benannt.',
        },
        {
            title: 'Schritt 2: Unternehmerblick (ca. 5–8 Min.)',
            body: 'Kernfrage aus dem Vorab-Bogen: „Wenn du der Unternehmer / die Chefin wärst — was würdest du persönlich in diesem Unternehmen ändern?“ Ziel: den Mitarbeiter aus der Mitarbeiterrolle herauslösen und unternehmerisches Denken anstoßen. Zuhören, nicht rechtfertigen — Ideen sammeln, später filtern.',
        },
        {
            title: 'Schritt 3: PEOne-Faktenabgleich (ca. 12–15 Min.)',
            body: 'Gemeinsamer Blick auf Multiplikator, VK-Quote (Ziel: ≥5 %, Top: ≥10 %) und Stundenumsatz (Basis vs. 75 % Auslastung). Stärken loben, Lücken sachlich markieren.',
        },
        {
            title: 'Schritt 4: Ursachen & Hebel (ca. 10–12 Min.)',
            body: 'Gemeinsam analysieren: Fehlt Produktwissen? Werden Veredelungen am Stuhl nicht angeboten oder aus Scheu an der Kasse unterkassiert? Konkrete Blockaden identifizieren — und prüfen, welche Impulse aus dem Unternehmerblick hier greifen.',
        },
        {
            title: 'Schritt 5: Maßnahmen & Protokoll (ca. 8–10 Min.)',
            body: 'Maximal zwei verbindliche Hebel schriftlich fixieren. Verbindlichen Follow-up-Termin in 4 bis 6 Wochen zur Zwischenkontrolle vereinbaren und gegenzeichnen.',
        },
    ] as Array<{ title: string; body: string }>,
    checks: [
        {
            title: '7 Tage Vorlauf mit Fragebogen',
            body: 'Mitarbeiter erhält vorab das druckbare Vorabblatt (4 Fragen): (1) Stärken, (2) Hürden, (3) Monatsziel, (4) Unternehmerblick. Verhindert Überraschungseffekte und öffnet unternehmerisches Denken schon vor dem Termin.',
        },
        {
            title: 'Geschützter, neutraler Raum',
            body: 'Niemals zwischen Spiegel und Waschbecken oder im Pausenraum. Ruhiger Tisch ohne Blickkontakt zum laufenden Salonbetrieb.',
        },
        {
            title: 'Feste Zeitbox (ca. 45–60 Min., variabel)',
            body: 'Richtwert, kein Dogma — bei klaren Fällen kürzer, bei komplexeren länger. Keine Hektik, aber Verbindlichkeit. Telefon und Kasse sind tabu; keine Störungen durch Kollegen.',
        },
        {
            title: 'Valide Datenbasis (PEOne)',
            body: 'Die PEOne-Auswertung liegt ausgedruckt vor. Datenbasis schlägt Bauchgefühl – partnerschaftlich und unvoreingenommen.',
        },
    ] as Array<{ title: string; body: string }>,
};

/**
 * Formblatt MG-01/02 — Ergebnis-Protokoll (DIN-A4, jsPDF).
 * Enthält ausschließlich Fakten, Zielvereinbarung und Unterschriften —
 * keinen Gesprächs-Leitfaden (der bleibt Ansichtssache im Modal).
 */
export function generateMgFormblattPdf(params: GenerateMgFormblattPdfParams): BuildMgFormblattPdfResult {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageW = 210;
    const pageH = 297;
    const m = 14;
    const cw = pageW - m * 2;
    const currency = params.currencyCode || 'EUR';
    const datum = params.datum || todayDe();
    const k = params.kennzahlen;
    let pageNum = 1;

    const drawPageChrome = () => {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(100, 116, 139);
        doc.text(
            'PreisExpert • Software & Unternehmensberatung für Friseure',
            m,
            8
        );
        doc.text('PEOne Standard', pageW - m, 8, { align: 'right' });
        doc.setDrawColor(203, 213, 225);
        doc.setLineWidth(0.3);
        doc.line(m, 10, pageW - m, 10);

        doc.setFontSize(8.5);
        doc.setTextColor(100, 116, 139);
        doc.text(
            `PreisExpert • Formblatt MG-01/02 • Entwicklungsgespräch-Protokoll | Gültig ab 2026 | Seite ${pageNum}`,
            pageW / 2,
            pageH - 8,
            { align: 'center' }
        );
    };

    // ========== PROTOKOLL (Seite 1) ==========
    drawPageChrome();
    let y = 17;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(180, 138, 86);
    doc.text('PREISEXPERT • PEONE ENTWICKLUNGSGESPRÄCH', m, y);
    y += 7;
    doc.setFontSize(17);
    doc.setTextColor(15, 23, 42);
    doc.text('Ergebnis-Protokoll & Zielvereinbarung', m, y);
    y += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(71, 85, 105);
    doc.text('Kennzahlenabgleich auf Basis der PEOne-Auswertung & wirtschaftliche Standortsicherung', m, y);
    y += 8;

    // Kopfdaten-Raster
    const colW = cw / 4;
    const headRows: [string, string][] = [
        ['Mitarbeiter / Stylist', params.employeeName || '—'],
        ['Gesprächsführer / Inhaber', params.inhaberName || '—'],
        ['Auswertungszeitraum', params.zeitraum || '—'],
        ['Datum', datum],
    ];
    doc.setFillColor(248, 250, 252);
    doc.rect(m, y, cw, 18, 'F');
    doc.setDrawColor(203, 213, 225);
    doc.rect(m, y, cw, 18, 'S');
    headRows.forEach((row, i) => {
        const x = m + i * colW;
        if (i > 0) {
            doc.line(x, y, x, y + 18);
        }
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(100, 116, 139);
        doc.text(row[0], x + 2, y + 5.5);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(15, 23, 42);
        const lines = doc.splitTextToSize(row[1], colW - 4);
        doc.text(lines.slice(0, 2), x + 2, y + 11.5);
    });
    y += 24;

    // Abschnitt 1
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(15, 23, 42);
    doc.text('1. KENNZAHLEN-FAKTENABGLEICH (PEONE-AUSWERTUNG)', m, y);
    y += 6;

    const diffMultiplikator = k.istMultiplikator - k.multiplikator;
    const diffVk = k.vkQuote - k.vkZiel;
    const diffAuslastung = k.umsatzauslastung - k.auslastungZiel;
    const diffStdUmsatz = k.istStdUmsatz - k.basisStdUmsatz;
    const diffUmsatzNetto = k.umsatzIstNetto - k.umsatzZielNetto;

    /** Differenz + Zonen-Einordnung (Dynamisierung): "−0,92 x (74 % – Kritisch)" in Zonenfarbe */
    const mkDiff = (
        type: 'faktor' | 'vk' | 'auslastung',
        ist: number,
        ziel: number,
        diffText: string
    ): { text: string; rgb: [number, number, number] } => {
        const level = getMgKpiLevel(type, ist, ziel);
        const pct = Math.round(getMgKpiErreichtQuote(ist, ziel) * 100);
        return { text: `${diffText} (${pct} % – ${level.label})`, rgb: level.rgb };
    };

    const kennRows: Array<{ label: string; ziel: string; wert: string; diff?: { text: string; rgb: [number, number, number] }; bedeutung: string }> = [
        {
            label: 'Dienstleistung (DL) / Multiplikator',
            ziel: `Ziel-Faktor: ${fmtNum(k.multiplikator, 2)} x`,
            wert: `${fmtNum(k.istMultiplikator, 2)} x`,
            diff: mkDiff('faktor', k.istMultiplikator, k.multiplikator, `${fmtSigned(diffMultiplikator, 2)} x`),
            bedeutung:
                'Tatsächlich erarbeiteter Faktor (Netto-Umsatz ÷ Bruttolohn) gegenüber dem betriebsbedingten Ziel-Faktor. Belegt sofort, ob der Mitarbeiter seine Vollkosten einspielt.',
        },
        {
            label: 'Verkauf (VK-Quote)',
            ziel: `Mind. 5 % | Ziel: ${fmtPct(k.vkZiel)}`,
            wert: fmtPct(k.vkQuote),
            diff: mkDiff('vk', k.vkQuote, k.vkZiel, fmtPkt(diffVk)),
            bedeutung:
                'Gemessen am DL-Umsatz. Zeigt verlässlich, ob Heimpflege konsequent beraten wird oder Geld ungenutzt am Stuhl liegen bleibt.',
        },
        {
            label: 'Umsatzauslastung',
            ziel: `Planwert: ${fmtPct(k.auslastungZiel)}`,
            wert: fmtPct(k.umsatzauslastung),
            diff: mkDiff('auslastung', k.umsatzauslastung, k.auslastungZiel, fmtPkt(diffAuslastung)),
            bedeutung:
                'In PEOne wird rein die Umsatzauslastung erfasst (keine Kalenderbelegung). Zeigt, zu wie viel % das finanzielle Soll erbracht wurde.',
        },
        {
            label: 'Stundenumsatz (Brutto)',
            ziel: `Basis-Soll (100 %): ${fmtEur(k.basisStdUmsatz, currency)}`,
            wert: fmtEur(k.istStdUmsatz, currency),
            diff: mkDiff('faktor', k.istStdUmsatz, k.basisStdUmsatz, `${fmtSigned(diffStdUmsatz, 2)} ${currency} / Std`),
            bedeutung:
                'Tatsächlich erwirtschafteter Stundenumsatz gegenüber dem kalkulierten Mindestumsatz zur Deckung aller Fixkosten und des Grundgehalts.',
        },
        {
            label: 'Monatsumsatz (Netto, Ø)',
            ziel: `Soll: ${fmtEur(k.umsatzZielNetto, currency)}`,
            wert: fmtEur(k.umsatzIstNetto, currency),
            diff: mkDiff('faktor', k.umsatzIstNetto, k.umsatzZielNetto, `${fmtSigned(diffUmsatzNetto, 0)} ${currency}`),
            bedeutung:
                'Reale Einnahmen gegenüber dem kalkulierten Soll. Die Differenz zeigt exakt, wie viel Umsatz pro Monat zum Ziel fehlt bzw. übertroffen wird.',
        },
        {
            label: 'Stundenumsatz (75 % Auslastung)',
            ziel: 'Ausgewiesener PEOne-Wert',
            wert: fmtEur(k.stdUmsatz75, currency),
            bedeutung:
                'Bei 75 % Umsatzauslastung muss der Stylist in den produktiven Stunden entsprechend mehr Umsatz erzielen, um im Jahresschnitt auf das Basis-Stundenziel zu kommen.',
        },
        {
            label: 'Durchschnittsbon (Ø-Bon)',
            ziel: 'Salondurchschnitt + Trend',
            wert: String(params.durchschnittsBon || '—').trim() || '—',
            bedeutung:
                'Dokumentiert, ob Farbveredelungen, Pflegebehandlungen und Mehraufwände aktiv angeboten und abrechnungssicher kassiert werden.',
        },
        {
            label: 'Kundenquote / Frequenz',
            ziel: 'Stamm- vs. Neukunden',
            wert: String(params.kundenquote || '—').trim() || '—',
            bedeutung: 'Reale Wiedervorlagequote und Kundenbindung im erfassten Zeitraum.',
        },
    ];

    const c1 = 40;
    const c2 = 34;
    const c3 = 36;
    const c4 = cw - c1 - c2 - c3;

    const drawTableHeader = () => {
        doc.setFillColor(30, 41, 59);
        doc.rect(m, y, cw, 9, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9.5);
        doc.setTextColor(255, 255, 255);
        doc.text('PEOne-Kennzahl', m + 1.5, y + 5.8);
        doc.text('Zielwert / Richtwert', m + c1 + 1.5, y + 5.8);
        doc.text('IST-Wert & Einordnung', m + c1 + c2 + 1.5, y + 5.8);
        doc.text('Betriebswirtschaftliche Bedeutung', m + c1 + c2 + c3 + 1.5, y + 5.8);
        y += 9;
    };

    drawTableHeader();

    kennRows.forEach((row, idx) => {
        const labelLines = doc.splitTextToSize(row.label, c1 - 3);
        const zielLines = doc.splitTextToSize(row.ziel, c2 - 3);
        const wertLines = doc.splitTextToSize(row.wert, c3 - 3);
        const diffLines = row.diff ? doc.splitTextToSize(row.diff.text, c3 - 3) : [];
        const bedLines = doc.splitTextToSize(row.bedeutung, c4 - 3);
        const rowH = Math.max(labelLines.length, zielLines.length, wertLines.length + diffLines.length, bedLines.length) * 3.9 + 3.5;

        if (y + rowH > pageH - 22) {
            doc.addPage();
            pageNum += 1;
            drawPageChrome();
            y = 16;
            drawTableHeader();
        }

        if (idx % 2 === 0) {
            doc.setFillColor(248, 250, 252);
            doc.rect(m, y, cw, rowH, 'F');
        }
        doc.setDrawColor(226, 232, 240);
        doc.rect(m, y, cw, rowH, 'S');
        doc.line(m + c1, y, m + c1, y + rowH);
        doc.line(m + c1 + c2, y, m + c1 + c2, y + rowH);
        doc.line(m + c1 + c2 + c3, y, m + c1 + c2 + c3, y + rowH);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9.5);
        doc.setTextColor(15, 23, 42);
        doc.text(labelLines, m + 1.5, y + 4.5);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(71, 85, 105);
        doc.text(zielLines, m + c1 + 1.5, y + 4.5);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9.5);
        doc.setTextColor(15, 23, 42);
        doc.text(wertLines, m + c1 + c2 + 1.5, y + 4.5);
        if (row.diff) {
            doc.setFontSize(9);
            doc.setTextColor(row.diff.rgb[0], row.diff.rgb[1], row.diff.rgb[2]);
            doc.text(diffLines, m + c1 + c2 + 1.5, y + 4.5 + wertLines.length * 3.9);
        }
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        doc.setTextColor(71, 85, 105);
        doc.text(bedLines, m + c1 + c2 + c3 + 1.5, y + 4.5);
        y += rowH;
    });

    // Abschnitt 2: Zielvereinbarung
    const ensureSpace = (needed: number) => {
        if (y + needed > pageH - 22) {
            doc.addPage();
            pageNum += 1;
            drawPageChrome();
            y = 16;
        }
    };

    ensureSpace(70);
    y += 8;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(15, 23, 42);
    doc.text('2. VERBINDLICHE ZIELVEREINBARUNG FÜR DIE NÄCHSTEN 3 MONATE', m, y);
    y += 7;

    const drawMassnahme = (label: string, value: string) => {
        ensureSpace(26);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(15, 23, 42);
        doc.text(label, m, y);
        y += 3.5;
        doc.setDrawColor(148, 163, 184);
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(m, y, cw, 17, 1, 1, 'FD');
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9.5);
        doc.setTextColor(15, 23, 42);
        const lines = doc.splitTextToSize(value || ' ', cw - 4);
        doc.text(lines.slice(0, 3), m + 2, y + 6);
        y += 21;
    };

    drawMassnahme(
        'Maßnahme 1 (z. B. VK-Quote auf mind. 5 % anheben durch konsequente Heimpflegeberatung):',
        params.massnahme1
    );
    drawMassnahme(
        'Maßnahme 2 (z. B. Erreichen des 75 %-Stundenumsatzes durch lückenloses Einbuchen von Veredelungen):',
        params.massnahme2
    );

    // Folgetermin / Zwischenkontrolle
    ensureSpace(22);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(15, 23, 42);
    doc.text('Nächstes Gespräch / Zwischenkontrolle am:', m, y);
    y += 3.5;
    doc.setDrawColor(148, 163, 184);
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(m, y, cw, 12, 1, 1, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(15, 23, 42);
    doc.text(params.folgeTermin?.trim() || '____________________', m + 3, y + 8);
    y += 16;

    // Unterschriften
    ensureSpace(48);
    y += 6;
    const sigW = (cw - 6) / 2;
    doc.setDrawColor(203, 213, 225);
    doc.line(m, y + 12, m + sigW, y + 12);
    doc.line(m + sigW + 6, y + 12, m + cw, y + 12);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(71, 85, 105);
    doc.text('Unterschrift Mitarbeiter / Stylist', m, y + 17);
    doc.text('Unterschrift Saloninhaber / Führungskraft', m + sigW + 6, y + 17);
    y += 27;
    doc.line(m, y, m + 60, y);
    doc.text('Ort, Datum', m, y + 4.5);

    const safeName = String(params.employeeName || 'Mitarbeiter')
        .replace(/[^a-zA-Z0-9äöüÄÖÜß_-]/g, '_')
        .replace(/_+/g, '_');
    const filename = `PEOne_MG01-02_${safeName}_${datum.replace(/\./g, '-')}.pdf`;
    const blob = doc.output('blob');
    return { doc, blob, filename };
}

/** Speichert das Protokoll-PDF als Download */
export function downloadMgFormblattPdf(params: GenerateMgFormblattPdfParams): void {
    const { blob, filename } = generateMgFormblattPdf(params);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export interface GenerateMgVorabFragebogenParams {
    employeeName: string;
    inhaberName?: string;
    gespraechsDatum?: string;
    salonName?: string;
}

/**
 * Formblatt MG-00 — Vorab-Fragebogen für den Mitarbeiter (DIN-A4, 1 Seite).
 * Zum Ausdrucken und ca. 7 Tage vor dem Entwicklungsgespräch aushändigen.
 * Layout füllt die Seite bewusst aus (weder zu eng noch auf 2 Seiten).
 */
export function generateMgVorabFragebogenPdf(
    params: GenerateMgVorabFragebogenParams
): BuildMgFormblattPdfResult {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageW = 210;
    const pageH = 297;
    const m = 14;
    const cw = pageW - m * 2;
    const datum = todayDe();
    const employee = String(params.employeeName || 'Mitarbeiter').trim() || 'Mitarbeiter';
    const gespraechsDatum = String(params.gespraechsDatum || '').trim();
    const salon = String(params.salonName || '').trim();
    const inhaber = String(params.inhaberName || '').trim();

    // Kopf
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(100, 116, 139);
    doc.text('PreisExpert • Software & Unternehmensberatung für Friseure', m, 9);
    doc.text('PEOne Standard', pageW - m, 9, { align: 'right' });
    doc.setDrawColor(203, 213, 225);
    doc.setLineWidth(0.3);
    doc.line(m, 11, pageW - m, 11);

    let y = 18;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(180, 138, 86);
    doc.text('PREISEXPERT • PEONE ENTWICKLUNGSGESPRÄCH', m, y);
    y += 7;
    doc.setFontSize(15);
    doc.setTextColor(15, 23, 42);
    doc.text('Vorab-Fragebogen zur Selbsteinschätzung', m, y);
    y += 5.5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(71, 85, 105);
    doc.text('Formblatt MG-00 · Bitte ca. 7 Tage vor dem Gespräch ausfüllen und mitbringen', m, y);
    y += 7;

    // Stammdaten-Box
    doc.setDrawColor(203, 213, 225);
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(m, y, cw, 18, 1.5, 1.5, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(71, 85, 105);
    doc.text('Mitarbeiter / Stylist', m + 3, y + 5.5);
    doc.text('Gesprächsdatum', m + cw * 0.55, y + 5.5);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(15, 23, 42);
    doc.text(employee, m + 3, y + 11.5);
    doc.text(gespraechsDatum || '____________________', m + cw * 0.55, y + 11.5);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    const metaBits = [
        salon ? `Salon: ${salon}` : '',
        inhaber ? `Gesprächsführer: ${inhaber}` : '',
    ].filter(Boolean);
    doc.text(metaBits.join('  ·  ') || 'Bitte dieses Blatt handschriftlich ausfüllen.', m + 3, y + 15.5);
    y += 23;

    // Intro
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(51, 65, 85);
    const intro = doc.splitTextToSize(
        'Dieses Blatt hilft dir, dich auf unser gemeinsames Entwicklungsgespräch vorzubereiten. Es gibt keine „richtigen“ oder „falschen“ Antworten — uns interessiert deine ehrliche Einschätzung. Nimm dir ruhig 10–15 Minuten Zeit.',
        cw
    );
    doc.text(intro, m, y);
    y += intro.length * 4 + 6;

    // Fragen: 4 Schreiblinien, großzügig — bleibt auf 1 Seite und füllt sie
    const answerLines = 4;
    const lineGap = 7.2;
    const questionGap = 5;
    MG_VORAB_FRAGEN.forEach((q) => {
        doc.setFillColor(15, 23, 42);
        doc.circle(m + 4, y + 2.2, 3.8, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.setTextColor(255, 255, 255);
        doc.text(String(q.nr), m + 4, y + 3.6, { align: 'center' });

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10.5);
        doc.setTextColor(15, 23, 42);
        const titleLines = doc.splitTextToSize(q.title, cw - 13);
        doc.text(titleLines, m + 11, y + 3);
        y += Math.max(7.5, titleLines.length * 4.2);

        doc.setFont('helvetica', 'italic');
        doc.setFontSize(8);
        doc.setTextColor(100, 116, 139);
        const hintLines = doc.splitTextToSize(q.hint, cw - 13);
        doc.text(hintLines, m + 11, y);
        y += hintLines.length * 3.5 + 2;

        doc.setDrawColor(203, 213, 225);
        doc.setLineWidth(0.3);
        for (let i = 0; i < answerLines; i++) {
            doc.line(m + 11, y, m + cw, y);
            y += lineGap;
        }
        y += questionGap;
    });

    // Footer-Hinweis — ans untere Drittel ziehen, falls noch Luft
    const hintBoxH = 14;
    const footerY = pageH - 12;
    const minHintY = y;
    const preferredHintY = footerY - hintBoxH - 4;
    y = Math.max(minHintY, Math.min(preferredHintY, pageH - 30));

    doc.setDrawColor(230, 194, 153);
    doc.setFillColor(255, 251, 245);
    doc.roundedRect(m, y, cw, hintBoxH, 1.5, 1.5, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(146, 104, 58);
    doc.text('Hinweis für dich', m + 3, y + 5);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(71, 85, 105);
    doc.text(
        'Bring dieses Blatt bitte ausgefüllt zum Gespräch mit. Wir nutzen es als Grundlage — nicht als Bewertung.',
        m + 3,
        y + 10
    );

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text(
        `PreisExpert • Formblatt MG-00 • Vorab-Fragebogen | Erstellt ${datum}`,
        pageW / 2,
        pageH - 7,
        { align: 'center' }
    );

    const safeName = employee
        .replace(/[^a-zA-Z0-9äöüÄÖÜß_-]/g, '_')
        .replace(/_+/g, '_');
    const filename = `PEOne_MG00_Vorab_${safeName}_${datum.replace(/\./g, '-')}.pdf`;
    const blob = doc.output('blob');
    return { doc, blob, filename };
}

/** Speichert den Vorab-Fragebogen als Download */
export function downloadMgVorabFragebogenPdf(params: GenerateMgVorabFragebogenParams): void {
    const { blob, filename } = generateMgVorabFragebogenPdf(params);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
}
